/**
 * "Is the Todoist token working?" as a button rather than a console
 * incantation. Also the first place a 401/503 from the Worker becomes a
 * sentence you can act on.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { pb } from "@/lib/pocketbase";
import { pingTodoist, todoistUnavailableReason } from "@/lib/todoistClient";
import { runTodoistSync, type SyncResult } from "@/lib/todoistSync";

type State =
  | { kind: "idle" }
  | { kind: "checking" }
  | { kind: "ok"; who: string }
  | { kind: "error"; message: string };

export function TodoistStatus() {
  const [state, setState] = useState<State>({ kind: "idle" });
  const blocked = todoistUnavailableReason();

  async function check() {
    setState({ kind: "checking" });
    try {
      const me = await pingTodoist();
      setState({ kind: "ok", who: me.name || me.email });
    } catch (err) {
      setState({ kind: "error", message: err instanceof Error ? err.message : String(err) });
    }
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
      {state.kind === "ok" && (
        <span style={{ fontSize: 12.5, color: "var(--good)" }}>Connected as {state.who}</span>
      )}
      {state.kind === "error" && (
        <span style={{ fontSize: 12.5, color: "var(--bad)", maxWidth: 340, textAlign: "right" }}>
          {explain(state.message)}
        </span>
      )}
      <button
        type="button"
        onClick={() => void check()}
        disabled={state.kind === "checking" || blocked !== null}
        title={blocked ?? undefined}
        style={{
          padding: "7px 14px", borderRadius: 999, border: "1px solid var(--line2)",
          background: "var(--surface)", fontSize: 12.5,
          color: blocked ? "var(--muted3)" : "var(--ink2)",
          cursor: blocked ? "not-allowed" : "pointer",
        }}
      >
        {state.kind === "checking" ? "Checking…" : "Test connection"}
      </button>
    </div>
  );
}

/**
 * The Worker's errors are accurate but terse. Each of these maps to a specific
 * setup step, so say which one rather than echoing the raw message.
 */
function explain(message: string): string {
  if (message.includes("TODOIST_TOKEN is not set")) {
    return "No token on the Worker yet — add TODOIST_TOKEN as a secret in the Cloudflare dashboard.";
  }
  if (message.includes("Sign in first")) {
    return "The Worker didn't accept this session. Sign out and back in, and check POCKETBASE_URL is set.";
  }
  if (message.includes("401") || message.includes("403")) {
    return "Todoist rejected the token — it may be wrong or revoked. Re-copy it from Todoist → Settings → Integrations → Developer.";
  }
  return message;
}


/* -------------------------------------------------------------------------- */

type SyncState =
  | { kind: "idle" }
  | { kind: "running" }
  | { kind: "done"; result: SyncResult }
  | { kind: "error"; message: string };

/**
 * Pulls Todoist into PocketBase on demand. There is no cron yet, so this is how
 * the mirror runs — see the note at the top of lib/todoistSync.ts for why the
 * sync lives in the browser rather than in the Worker.
 */
export function TodoistSync() {
  const [state, setState] = useState<SyncState>({ kind: "idle" });
  const queryClient = useQueryClient();
  const blocked = todoistUnavailableReason();

  async function sync() {
    const userId = (pb.authStore.model as { id?: string } | null)?.id;
    if (!userId) return setState({ kind: "error", message: "Sign in first." });

    setState({ kind: "running" });
    try {
      const result = await runTodoistSync(userId);
      setState({ kind: "done", result });
      // Everything on screen reads these collections, so refetch rather than
      // making the user reload to see what just landed.
      await queryClient.invalidateQueries();
    } catch (err) {
      setState({ kind: "error", message: err instanceof Error ? err.message : String(err) });
    }
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
      {state.kind === "done" && <SyncSummary result={state.result} />}
      {state.kind === "error" && (
        <span style={{ fontSize: 12.5, color: "var(--bad)", maxWidth: 340, textAlign: "right" }}>
          {state.message}
        </span>
      )}
      <button
        type="button"
        onClick={() => void sync()}
        disabled={state.kind === "running" || blocked !== null}
        title={blocked ?? undefined}
        style={{
          padding: "7px 14px", borderRadius: 999, border: "1px solid var(--line2)",
          background: "var(--surface)", fontSize: 12.5,
          color: blocked ? "var(--muted3)" : "var(--ink2)",
          cursor: blocked ? "not-allowed" : "pointer",
        }}
      >
        {state.kind === "running" ? "Syncing…" : "Sync now"}
      </button>
    </div>
  );
}

function SyncSummary({ result }: { result: SyncResult }) {
  const { projects, phases, items } = result;
  const changed =
    projects.created + projects.updated + projects.deleted +
    phases.created + phases.updated + phases.deleted +
    items.created + items.updated + items.deleted;

  return (
    <span style={{ fontSize: 12.5, textAlign: "right", maxWidth: 380 }}>
      <span style={{ color: result.errors.length ? "var(--warn)" : "var(--good)" }}>
        {changed === 0
          ? "Already up to date."
          : `${items.created} new, ${items.updated} updated, ${items.deleted} removed ` +
            `across ${projects.created + projects.updated} projects.`}
      </span>
      {result.orphanedEntries > 0 && (
        <span style={{ display: "block", color: "var(--warn)", marginTop: 3 }}>
          {result.orphanedEntries} time {result.orphanedEntries === 1 ? "entry" : "entries"} orphaned — the hours are kept.
        </span>
      )}
      {result.errors.length > 0 && (
        <span style={{ display: "block", color: "var(--bad)", marginTop: 3 }}>
          {result.errors.length} problem{result.errors.length === 1 ? "" : "s"}: {result.errors[0]}
        </span>
      )}
    </span>
  );
}
